import React, { useState } from "react";
import type { ViewMode } from "./RoomSectionIsoViewer";
import {
  ActionIcon,
  Alert,
  Box,
  Card,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { RoomSectionIsoViewer } from "./RoomSectionIsoViewer";
import type {
  RoomSectionCameraDirs,
  RoomSectionCenterCoordinate,
  RoomSectionDirection,
  IBoundingboxViewer3dItem,
} from "./types";

export type RoomSectionViewsPanelProps = {
  cameraDirs: RoomSectionCameraDirs;
  renderItems: IBoundingboxViewer3dItem[];
  sectionCenterCoordinate: RoomSectionCenterCoordinate | null;
  selectedSection: RoomSectionDirection;
  onSelectedSectionChange: (section: RoomSectionDirection) => void;
  onItemClick?: (item: IBoundingboxViewer3dItem["item"]) => void;
  title?: string;
  emptyText?: string;
  background?: string;
  headerControls?: React.ReactNode;
};

export const getRoomSectionTitle = (
  selectedSection: RoomSectionDirection,
): string =>
  selectedSection === "sideA"
    ? "Seksjon A"
    : selectedSection === "sideB"
      ? "Seksjon B"
      : selectedSection === "sideC"
        ? "Seksjon C"
        : selectedSection === "sideD"
          ? "Seksjon D"
          : "Isometrisk";

const tabItems: Array<{ label: string; value: RoomSectionDirection }> = [
  { label: "A", value: "sideA" },
  { label: "B", value: "sideB" },
  { label: "C", value: "sideC" },
  { label: "D", value: "sideD" },
  { label: "ISO", value: "isometric" },
];

export const RoomSectionViewsPanel: React.FC<RoomSectionViewsPanelProps> = ({
  cameraDirs,
  renderItems,
  sectionCenterCoordinate,
  selectedSection,
  onSelectedSectionChange,
  onItemClick,
  title = "Seksjonsvisninger",
  emptyText = "Ingen seksjonsdata tilgjengelig for valgt rom ennå.",
  background = "#f3f4f6",
  headerControls,
}) => {
  const [opened, setOpened] = useState(false);
  const [isoViewMode, setIsoViewMode] = useState<ViewMode>("orbit");
  const sectionTitle = getRoomSectionTitle(selectedSection);
  const cameraDir = cameraDirs[selectedSection];

  if (!sectionCenterCoordinate) {
    return (
      <Alert color="gray" variant="light">
        <Stack gap={4}>
          <Text fw={600}>{title}</Text>
          <Text size="sm" c="dimmed">
            {emptyText}
          </Text>
        </Stack>
      </Alert>
    );
  }

  return (
    <>
      <Box
        h="100%"
        px="md"
        py="md"
        bg={background}
        style={{ overflowY: "auto" }}
      >
        <Stack gap="md">
          <Group justify="space-between" align="center" gap="sm">
            <Text fw={700}>{title}</Text>
            {headerControls}
          </Group>

          <SegmentedControl
            fullWidth
            value={selectedSection}
            onChange={(value: string) =>
              onSelectedSectionChange(value as RoomSectionDirection)
            }
            data={tabItems}
          />

          <Card withBorder radius="md" p="md">
            <Stack gap="sm">
              <Group justify="space-between" align="center">
                <Text fw={700}>{sectionTitle}</Text>
                <Tooltip label="Åpne i full visning">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={`Åpne ${sectionTitle} i full visning`}
                    onClick={() => setOpened(true)}
                  >
                    ↗
                  </ActionIcon>
                </Tooltip>
              </Group>

              <Box
                h={280}
                style={{
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#f8f9fa",
                }}
              >
                <RoomSectionIsoViewer
                  cameraDir={cameraDir}
                  renderItems={renderItems}
                  sectionCenterCoordinate={sectionCenterCoordinate}
                  selectedSection={selectedSection}
                  onItemClick={onItemClick}
                  viewMode={isoViewMode}
                  onViewModeChange={setIsoViewMode}
                />
              </Box>
            </Stack>
          </Card>
        </Stack>
      </Box>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={sectionTitle}
        size="90vw"
        centered
      >
        <Box h="75vh" w="100%">
          <RoomSectionIsoViewer
            cameraDir={cameraDir}
            renderItems={renderItems}
            sectionCenterCoordinate={sectionCenterCoordinate}
            selectedSection={selectedSection}
            onItemClick={onItemClick}
            viewMode={isoViewMode}
            onViewModeChange={setIsoViewMode}
          />
        </Box>
      </Modal>
    </>
  );
};
